import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

import { calculerCriticite } from "../NC/criticiteHelper";

export const MAX_FILE_SIZE = 10 * 1024 * 1024;
export const MAX_IMPORT_ROWS = 1000;

export const IMPORT_COLUMNS = [
  { key: "processus", label: "Processus", required: true, width: 42 },
  { key: "constat_date", label: "Date de constat", required: true, width: 18 },
  { key: "direction", label: "Direction", required: true, width: 34 },
  { key: "trigger", label: "Type de déclencheur", required: true, width: 32 },
  { key: "constat_user", label: "Constaté par", width: 34 },
  { key: "description_constat", label: "Description", required: true, width: 48 },
  { key: "exigence", label: "Type d’exigence non respectée", required: true, width: 38 },
  { key: "description_exigence", label: "Description exigence non respectée", required: true, width: 48 },
  { key: "gravite", label: "Gravité", required: true, width: 42 },
  { key: "frequence", label: "Fréquence", required: true, width: 56 },
  { key: "curatif_description", label: "Action curative", width: 44 },
  { key: "curatif_responsable", label: "Qui agit", width: 34 },
  { key: "curatif_date", label: "Date de réalisation", width: 20 },
  { key: "curatif_statut", label: "Statut action curative", width: 24 },
];

export const GRAVITE_OPTIONS = [
  { label: "1 — Mineure (sans impact opérationnel)", value: "1" },
  { label: "2 — Modérée (impact possible processus)", value: "2" },
  { label: "3 — Sérieuse (impact réel / Non respect KPI)", value: "3" },
  { label: "4 — Critique (impact légal / bénéficiaires)", value: "4" },
];

export const FREQUENCE_OPTIONS = [
  { label: "1 — Jamais (aucun antécédent)", value: "1" },
  { label: "2 — Rare (1 fois dans l'année)", value: "2" },
  { label: "3 — Prévisible (plusieurs fois par an)", value: "3" },
  { label: "4 — Récurrent (écart récurrent sur la prestation durant les 3 derniers mois glissants)", value: "4" },
];

export const CURATIF_STATUS_OPTIONS = [
  { label: "À faire", value: "a_faire" },
  { label: "En cours", value: "en_cours" },
  { label: "Réalisée", value: "realisee" },
];

export const processLabel = (item) =>
  `${String(item?.instance || "Référence non renseignée").trim()} — ${String(
    item?.referentiel_nom || "Référentiel non renseigné"
  ).trim()} (${String(item?.direction_code || "—").trim()})`;

export const directionLabel = (item) =>
  [item?.code, item?.name]
    .map((value) => String(value || "").trim())
    .filter(Boolean)
    .join(" — ") || "Direction sans nom";

export const triggerLabel = (item) =>
  `${String(item?.trigger_code || "—").trim()} — ${String(
    item?.trigger_name || "Déclencheur sans nom"
  ).trim()}`;

export const exigenceLabel = (item) => {
  const code = String(item?.exigence_code || "").trim();
  const name = String(item?.exigence_name || "").trim();
  return [code ? `[${code}]` : "", name].filter(Boolean).join(" ");
};

export const userLabel = (item) => {
  const name = [item?.first_name, item?.last_name]
    .map((value) => String(value || "").trim())
    .filter(Boolean)
    .join(" ");
  return name || String(item?.identity || item?.label || "Utilisateur sans nom").trim();
};

export const userOptionLabel = (item) => {
  const email = String(item?.email || item?.personnal_email || "-").trim();
  const registrationNumber = String(item?.registration_number || "-").trim();
  return `${userLabel(item)} — ${registrationNumber} — ${email}`;
};

export const normalizeValue = (value) =>
  String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLocaleLowerCase("fr");

const normalizeHeader = (value) =>
  normalizeValue(value).replace(/[’']/g, "'");

const textValue = (value) => {
  if (value === null || value === undefined) return "";
  if (value instanceof Date) return value;
  if (typeof value === "object") {
    if (Array.isArray(value.richText)) {
      return value.richText.map((part) => part.text || "").join("");
    }
    if (value.result !== undefined) return textValue(value.result);
    if (value.text !== undefined) return String(value.text).trim();
  }
  return String(value).trim();
};

const pad = (value) => String(value).padStart(2, "0");

const datePartsToIso = (year, month, day) => {
  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null;
  }
  return `${year}-${pad(month)}-${pad(day)}`;
};

export const toIsoDate = (value) => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return datePartsToIso(value.getFullYear(), value.getMonth() + 1, value.getDate());
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    const excelEpoch = Date.UTC(1899, 11, 30);
    const date = new Date(excelEpoch + Math.round(value) * 86400000);
    return datePartsToIso(
      date.getUTCFullYear(),
      date.getUTCMonth() + 1,
      date.getUTCDate()
    );
  }

  const text = String(value || "").trim();
  let match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
  if (match) return datePartsToIso(Number(match[1]), Number(match[2]), Number(match[3]));
  match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(text);
  if (match) return datePartsToIso(Number(match[3]), Number(match[2]), Number(match[1]));
  return null;
};

const uniqueLabels = (values) => [...new Set(values.filter(Boolean))];

const referenceLists = (references) => ({
  processus: uniqueLabels(references.processus.map(processLabel)),
  directions: uniqueLabels(references.directions.map(directionLabel)),
  triggers: uniqueLabels(references.triggers.map(triggerLabel)),
  exigences: uniqueLabels(references.exigences.map(exigenceLabel)),
  gravites: GRAVITE_OPTIONS.map((item) => item.label),
  frequences: FREQUENCE_OPTIONS.map((item) => item.label),
  statuts: CURATIF_STATUS_OPTIONS.map((item) => item.label),
});

export const downloadImportTemplate = async (references) => {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "PGI - SMQ";
  workbook.created = new Date();
  const worksheet = workbook.addWorksheet("Import NC");
  const referenceSheet = workbook.addWorksheet("_References", {
    state: "veryHidden",
  });

  worksheet.columns = IMPORT_COLUMNS.map((column) => ({
    key: column.key,
    header: column.label,
    width: column.width,
  }));
  worksheet.views = [{ state: "frozen", ySplit: 1 }];
  worksheet.autoFilter = `A1:N1`;
  worksheet.getRow(1).height = 32;
  worksheet.getRow(1).eachCell((cell, index) => {
    const column = IMPORT_COLUMNS[index - 1];
    cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: column.required ? "FF173D61" : "FF4F6F8F" },
    };
    cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
  });
  [5, 12].forEach((column) => {
    worksheet.getCell(1, column).note =
      "Saisir uniquement le nom complet de l'utilisateur. L'association sera vérifiée après le dépôt.";
  });

  const lists = referenceLists(references);
  const listNames = {};
  Object.entries(lists).forEach(([key, values], columnIndex) => {
    const excelColumn = referenceSheet.getColumn(columnIndex + 1);
    excelColumn.values = [key, ...values];
    const rangeName = `Liste_${key}`;
    listNames[key] = rangeName;
    if (values.length) {
      const letter = excelColumn.letter;
      workbook.definedNames.add(
        `_References!$${letter}$2:$${letter}$${values.length + 1}`,
        rangeName
      );
    }
  });

  const validations = {
    1: "processus",
    3: "directions",
    4: "triggers",
    7: "exigences",
    9: "gravites",
    10: "frequences",
    14: "statuts",
  };
  for (let row = 2; row <= MAX_IMPORT_ROWS + 1; row += 1) {
    worksheet.getCell(row, 2).numFmt = "dd/mm/yyyy";
    worksheet.getCell(row, 13).numFmt = "dd/mm/yyyy";
    Object.entries(validations).forEach(([column, listKey]) => {
      if (!lists[listKey].length) return;
      worksheet.getCell(row, Number(column)).dataValidation = {
        type: "list",
        allowBlank: !IMPORT_COLUMNS[Number(column) - 1].required,
        formulae: [listNames[listKey]],
        showErrorMessage: true,
        errorTitle: "Valeur invalide",
        error: "Choisissez une valeur dans la liste.",
      };
    });
  }

  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(
    new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }),
    "modele_import_nc_smq.xlsx"
  );
};

export const readImportWorkbook = async (file) => {
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(await file.arrayBuffer());
  const worksheet = workbook.worksheets.find((sheet) => sheet.name !== "_References");
  if (!worksheet) throw new Error("Le classeur ne contient aucune feuille d’import.");

  const actualHeaders = IMPORT_COLUMNS.map((_, index) =>
    normalizeHeader(textValue(worksheet.getCell(1, index + 1).value))
  );
  const expectedHeaders = IMPORT_COLUMNS.map((column) => normalizeHeader(column.label));
  const invalidHeader = expectedHeaders.findIndex(
    (expected, index) => actualHeaders[index] !== expected
  );
  if (invalidHeader >= 0) {
    throw new Error(
      `Colonne ${invalidHeader + 1} invalide. Utilisez le modèle fourni sans modifier les en-têtes.`
    );
  }

  const rows = [];
  for (let rowNumber = 2; rowNumber <= worksheet.actualRowCount; rowNumber += 1) {
    const raw = {};
    IMPORT_COLUMNS.forEach((column, index) => {
      raw[column.key] = textValue(worksheet.getCell(rowNumber, index + 1).value);
    });
    const isEmpty = IMPORT_COLUMNS.every(
      (column) => normalizeValue(raw[column.key]) === ""
    );
    if (!isEmpty) rows.push({ raw, sourceRow: rowNumber });
  }

  if (!rows.length) throw new Error("Le fichier ne contient aucune NC à importer.");
  if (rows.length > MAX_IMPORT_ROWS) {
    throw new Error(`Le fichier dépasse la limite de ${MAX_IMPORT_ROWS} lignes.`);
  }
  return rows;
};

const buildIndex = (items, labelBuilder) => {
  const index = new Map();
  items.forEach((item) => {
    const key = normalizeValue(labelBuilder(item));
    index.set(key, [...(index.get(key) || []), item]);
  });
  return index;
};

const buildUserNameIndex = (users) => {
  const index = new Map();
  users.forEach((user) => {
    [
      [user?.first_name, user?.last_name],
      [user?.last_name, user?.first_name],
    ].forEach((parts) => {
      const key = normalizeValue(parts.filter(Boolean).join(" "));
      if (!key) return;
      const matches = index.get(key) || [];
      if (!matches.some((item) => item.user_id === user.user_id)) {
        index.set(key, [...matches, user]);
      }
    });
  });
  return index;
};

const hasOwn = (object, key) => Object.prototype.hasOwnProperty.call(object, key);

const resolveUserName = ({
  assignments,
  errors,
  index,
  label,
  required,
  role,
  users,
  value,
}) => {
  const normalized = normalizeValue(value);
  const key = normalized || `__missing_${role}`;

  if (!normalized && !required) return { issue: null, userId: null };

  if (hasOwn(assignments, key)) {
    const assignedId = assignments[key];
    if (assignedId === null && !required) return { issue: null, userId: null };
    const assignedUser = users.find(
      (user) => String(user.user_id) === String(assignedId)
    );
    if (assignedUser) return { issue: null, userId: assignedUser.user_id };
  }

  const matches = normalized ? index.get(normalized) || [] : [];
  if (matches.length === 1) return { issue: null, userId: matches[0].user_id };

  const reason = !normalized ? "missing" : matches.length > 1 ? "ambiguous" : "unknown";
  const message = {
    ambiguous: `${label} ambigu : choisissez l'utilisateur correspondant.`,
    missing: `${label} obligatoire.`,
    unknown: `${label} introuvable : associez ce nom à un utilisateur.`,
  }[reason];
  errors.push(message);
  return {
    issue: {
      input: String(value || "").trim(),
      key,
      optional: !required,
      reason,
      role,
    },
    userId: null,
  };
};

const resolveReference = (value, index, idKey, label, errors, required = true) => {
  const normalized = normalizeValue(value);
  if (!normalized) {
    if (required) errors.push(`${label} obligatoire.`);
    return null;
  }
  const matches = index.get(normalized) || [];
  if (matches.length === 0) {
    errors.push(`${label} introuvable dans les références actuelles.`);
    return null;
  }
  if (matches.length > 1) {
    errors.push(`${label} ambiguë ; contactez l’administrateur SMQ.`);
    return null;
  }
  return matches[0][idKey] ?? null;
};

const resolveOption = (value, options, label, errors, required = true) => {
  const normalized = normalizeValue(value);
  if (!normalized) {
    if (required) errors.push(`${label} obligatoire.`);
    return null;
  }
  const option = options.find(
    (item) =>
      normalizeValue(item.label) === normalized || normalizeValue(item.value) === normalized
  );
  if (!option) errors.push(`${label} invalide.`);
  return option?.value || null;
};

const duplicateKey = (raw, data) =>
  IMPORT_COLUMNS.map((column) => {
    if (column.key === "constat_user") return String(data.constat_user ?? "");
    if (column.key === "curatif_responsable") {
      return String(data.curatif_responsable ?? "");
    }
    if (["constat_date", "curatif_date"].includes(column.key)) {
      return toIsoDate(raw[column.key]) || normalizeValue(raw[column.key]);
    }
    return normalizeValue(raw[column.key]);
  }).join("\u001f");

export const validateImportRows = (sourceRows, references, assignments = {}) => {
  const indexes = {
    processus: buildIndex(references.processus, processLabel),
    directions: buildIndex(references.directions, directionLabel),
    triggers: buildIndex(references.triggers, triggerLabel),
    users: buildUserNameIndex(references.users),
    exigences: buildIndex(references.exigences, exigenceLabel),
  };
  const seen = new Map();

  return sourceRows.map(({ raw, sourceRow }) => {
    const errors = [];
    const constatDate = toIsoDate(raw.constat_date);
    if (!constatDate) errors.push("Date de constat invalide.");
    const descriptionConstat = String(raw.description_constat || "").trim();
    const descriptionExigence = String(raw.description_exigence || "").trim();
    if (!descriptionConstat) errors.push("Description obligatoire.");
    if (!descriptionExigence) errors.push("Description de l’exigence obligatoire.");

    const processusId = resolveReference(
      raw.processus,
      indexes.processus,
      "processus_id",
      "Processus",
      errors
    );
    const directionId = resolveReference(
      raw.direction,
      indexes.directions,
      "direction_id",
      "Direction",
      errors
    );
    const triggerId = resolveReference(
      raw.trigger,
      indexes.triggers,
      "trigger_id",
      "Type de déclencheur",
      errors
    );
    const userIssues = [];
    const constatUser = resolveUserName({
      assignments,
      errors,
      index: indexes.users,
      label: "Utilisateur ayant constaté",
      required: false,
      role: "constat_user",
      users: references.users,
      value: raw.constat_user,
    });
    if (constatUser.issue) userIssues.push(constatUser.issue);
    const exigenceId = resolveReference(
      raw.exigence,
      indexes.exigences,
      "exigence_id",
      "Type d’exigence",
      errors
    );
    const gravite = resolveOption(raw.gravite, GRAVITE_OPTIONS, "Gravité", errors);
    const frequence = resolveOption(
      raw.frequence,
      FREQUENCE_OPTIONS,
      "Fréquence",
      errors
    );

    const curativeValues = [
      raw.curatif_description,
      raw.curatif_responsable,
      raw.curatif_date,
      raw.curatif_statut,
    ];
    const hasCuratif = curativeValues.some((value) => normalizeValue(value) !== "");
    let curatifResponsable = null;
    let curatifDate = null;
    let curatifStatut = null;
    if (hasCuratif) {
      if (!String(raw.curatif_description || "").trim()) {
        errors.push("Description de l’action curative obligatoire.");
      }
      const resolvedResponsable = resolveUserName({
        assignments,
        errors,
        index: indexes.users,
        label: "Responsable de l’action curative",
        required: true,
        role: "curatif_responsable",
        users: references.users,
        value: raw.curatif_responsable,
      });
      curatifResponsable = resolvedResponsable.userId;
      if (resolvedResponsable.issue) userIssues.push(resolvedResponsable.issue);
      curatifDate = toIsoDate(raw.curatif_date);
      if (!curatifDate) errors.push("Date de réalisation curative invalide.");
      curatifStatut = resolveOption(
        raw.curatif_statut,
        CURATIF_STATUS_OPTIONS,
        "Statut de l’action curative",
        errors
      );
    }

    let criticite = null;
    if (gravite && frequence) {
      criticite = calculerCriticite(Number(gravite), Number(frequence));
    }
    const data = {
      constat_date: constatDate,
      constat_user: constatUser.userId,
      criticite,
      curatif_date: curatifDate,
      curatif_description: String(raw.curatif_description || "").trim(),
      curatif_responsable: curatifResponsable,
      curatif_statut: curatifStatut,
      description_constat: descriptionConstat,
      description_exigence: descriptionExigence,
      direction_id: directionId,
      exigence_id: exigenceId,
      frequence,
      gravite,
      processus_id: processusId,
      trigger_id: triggerId,
    };
    const key = duplicateKey(raw, data);
    const duplicateOf = seen.get(key) || null;
    if (!duplicateOf) seen.set(key, sourceRow);

    return {
      data,
      duplicateOf,
      errors,
      hasCuratif,
      importError: null,
      importStatus: duplicateOf ? "duplicate" : "ready",
      raw,
      sourceRow,
      userIssues,
      valid: errors.length === 0,
    };
  });
};

export const buildUserMappingGroups = (rows) => {
  const groups = new Map();
  rows.forEach((row) => {
    row.userIssues.forEach((issue) => {
      const group = groups.get(issue.key) || {
        canIgnore: true,
        count: 0,
        input: issue.input,
        key: issue.key,
        reasons: new Set(),
        roles: new Set(),
        sourceRows: new Set(),
      };
      group.canIgnore = group.canIgnore && issue.optional;
      group.count += 1;
      group.reasons.add(issue.reason);
      group.roles.add(issue.role);
      group.sourceRows.add(row.sourceRow);
      groups.set(issue.key, group);
    });
  });
  return [...groups.values()].map((group) => ({
    ...group,
    reasons: [...group.reasons],
    roles: [...group.roles],
    sourceRows: [...group.sourceRows].sort((a, b) => a - b),
  }));
};

export const toConstatPayload = (row) => ({
  constat_date: row.data.constat_date,
  constat_user: row.data.constat_user,
  criticite: row.data.criticite,
  description_constat: row.data.description_constat,
  description_exigence: row.data.description_exigence,
  direction_id: row.data.direction_id,
  exigence_id: row.data.exigence_id,
  frequence: row.data.frequence,
  gravite: row.data.gravite,
  processus_id: row.data.processus_id,
  trigger_id: row.data.trigger_id,
});

export const toCuratifPayload = (row) => ({
  date_realisation: row.data.curatif_date,
  description: row.data.curatif_description,
  responsable_id: row.data.curatif_responsable,
  statut: row.data.curatif_statut,
});
